import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { Badge } from "@trillion/ui/components/ui/badge";
import { ApiError } from "@trillion/ui/lib/api-client";
import { toast } from "sonner";
import { api } from "../lib/api";
import { serverError } from "../lib/server-error";

// The API's 409 text when this user's request is still waiting (DiagramSharingRules.AccessRequestPendingError).
const alreadyPendingError = "An access request is already pending";

/** The 409 the API returned: already pending, or already has access. */
interface Refusal { message: string; pending: boolean }

export function AccessRequestPage() {
  const { diagramId } = useParams<{ diagramId: string }>();
  const [isRequesting, setIsRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  async function handleRequest() {
    if (!diagramId) return;
    setIsRequesting(true);
    try {
      await api(`/api/diagrams/${diagramId}/access-requests`, {
        method: "POST",
      });
      setRequested(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const message = serverError(err, alreadyPendingError);
        setRefusal({ message, pending: message === alreadyPendingError });
      } else {
        toast.error("Failed to submit access request");
      }
    } finally {
      setIsRequesting(false);
    }
  }

  const showPending = requested || refusal?.pending === true;

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold">
              {requested ? "Access requested" : "Request access"}
            </CardTitle>
            <CardDescription>
              {requested
                ? "Your request was sent. A sharing manager will review it."
                : refusal
                  ? refusal.message
                  : "You do not have access to this diagram. Ask one of its sharing managers for access."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showPending && (
              <div className="flex justify-center">
                <Badge variant="secondary" className="text-sm">
                  Pending
                </Badge>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            {!requested && !refusal && (
              <Button
                className="w-full"
                onClick={handleRequest}
                disabled={isRequesting}
              >
                {isRequesting ? "Requesting..." : "Request access"}
              </Button>
            )}
            <Button asChild variant="outline" className="w-full">
              <Link to="/dashboard">Back to Dashboard</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    </main>
  );
}
